import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../api.js';
import { useAuth } from '../auth/store.js';

/**
 * The guest list.
 *
 * Exists so that onboarding a friend stops meaning an `.env` edit and a
 * container restart. Owner-only — the server enforces that; this page just
 * renders the refusal legibly when someone reaches it without the role.
 */
export function AllowlistPage() {
  const user = useAuth((s) => s.user);
  const [emails, setEmails] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const body = await api.get<{ emails: string[] }>('/api/allowlist');
      setEmails(body.emails);
      setError(null);
    } catch (err) {
      setEmails([]);
      setError(err instanceof ApiError ? err.message : 'Could not load the guest list.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    setPending(true);
    try {
      await api.post('/api/allowlist', { email: draft.trim() });
      setDraft('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that address.');
    } finally {
      setPending(false);
    }
  };

  const remove = async (email: string) => {
    setPending(true);
    try {
      await api.delete(`/api/allowlist/${encodeURIComponent(email)}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that address.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Guest list</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Anyone whose Google address is on this list can sign in. Removing someone takes effect
          immediately.
        </p>
      </header>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          {error}
        </p>
      )}

      <form onSubmit={add} className="flex gap-2">
        <label className="sr-only" htmlFor="allowlist-email">
          Email address
        </label>
        <input
          id="allowlist-email"
          type="email"
          required
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="friend@example.com"
          className="flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
        />
        <button
          type="submit"
          disabled={pending}
          className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium disabled:opacity-50 dark:border-neutral-700"
        >
          Add
        </button>
      </form>

      {emails === null ? (
        <p className="text-sm text-neutral-600 dark:text-neutral-400">Loading…</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
          {emails.length === 0 && (
            <li className="p-3 text-sm text-neutral-600 dark:text-neutral-400">
              Nobody is on the list.
            </li>
          )}
          {emails.map((email) => {
            const isSelf = user?.email.toLowerCase() === email.toLowerCase();
            return (
              <li key={email} className="flex items-center gap-3 p-3 text-sm">
                <span className="flex-1">{email}</span>
                {isSelf ? (
                  // The server refuses this too; saying so here avoids
                  // offering an action that can only fail.
                  <span className="text-neutral-500 dark:text-neutral-400">you</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void remove(email)}
                    disabled={pending}
                    className="underline underline-offset-2 disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
